import { Button } from "@/components/ui/button";
import { Branch } from "@/utils/types";
import axios from "axios";
import { toast } from "sonner";
import { useState } from "react";
import { useAxiosInstance } from "@/utils/useAxiosInstance";

export default function EditBranchInformation({
  setIsEditBranchInformation,
  initialBranch,
  setInitialBranch,
}: {
  setIsEditBranchInformation: (isEditBranchInformation: boolean) => void;
  initialBranch: Branch;
  setInitialBranch: (initialBranch: Branch) => void;
}) {
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [selectedBranch, setSelectedBranch] = useState<Branch>(initialBranch);

  const handleChange = (name: string, value: string) => {
    setSelectedBranch({ ...selectedBranch, [name]: value });
  };

  const handleChangeAddress = (name: string, value: string) => {
    setSelectedBranch({
      ...selectedBranch,
      address: { ...selectedBranch.address!, [name]: value },
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log("Form submitted");

    const requestBody = {
      name: selectedBranch.name,
      phone: selectedBranch.phone,
      owner_phone: selectedBranch.owner_phone,
      ai_phone: selectedBranch.ai_phone,
      email: selectedBranch.email,
      address: selectedBranch.address,
    };

    try {
      const storedUserData = localStorage.getItem("user_data");
      const userData = storedUserData ? JSON.parse(storedUserData) : null;
      const businessName = userData?.owner?.business_name;
      const response = await axiosInstance.patch(
        `api/v1/branch/${selectedBranch.id}/?business_name=${businessName}`,
        requestBody,
      );
      toast.success("Branch information updated successfully");
      setInitialBranch(selectedBranch);
      userData.owner.phone = selectedBranch.owner_phone;
      localStorage.setItem("user_data", JSON.stringify(userData));
      setIsEditBranchInformation(false);
    } catch (error: any) {
      console.error("Error updating branch information:", error);
      toast.error(
        "Error updating branch information : " +
          Object.values(error?.response?.data).join(", ") +
          " " +
          Object.keys(error?.response?.data).join(", "),
      );
    }
  };

  return (
    <div>
      <div className="my-4 rounded-lg bg-white p-4 shadow">
        <div className="mb-6 flex flex-col items-start justify-start md:mb-2 md:flex-row md:items-center md:justify-between">
          <h3 className="mb-2 text-lg font-semibold">
            Edit Branch's Information
          </h3>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2">
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-gray-600">
                Business Name:
              </label>
              <input
                type="text"
                className="rounded-md border border-gray-300 px-3 py-2 text-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                onChange={(e) => handleChange("name", e.target.value)}
                value={selectedBranch.name}
                placeholder="Type name here..."
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-gray-600">
                Business Phone:
              </label>
              <input
                type="text"
                className="rounded-md border border-gray-300 px-3 py-2 text-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                onChange={(e) => handleChange("phone", e.target.value)}
                value={selectedBranch.phone}
                placeholder="Type business phone here..."
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-gray-600">
                Owner Phone:
              </label>
              <input
                type="text"
                className="rounded-md border border-gray-300 px-3 py-2 text-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                value={selectedBranch.owner_phone}
                onChange={(e) => handleChange("owner_phone", e.target.value)}
                placeholder="Type owner phone here..."
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-gray-600">
                AI Agent Phone:
              </label>
              <input
                type="text"
                className="rounded-md border border-gray-300 px-3 py-2 text-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                value={selectedBranch.ai_phone}
                onChange={(e) => handleChange("ai_phone", e.target.value)}
                placeholder="Type ai agent phone here..."
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-gray-600">
                Business Email:
              </label>
              <input
                type="text"
                className="rounded-md border border-gray-300 px-3 py-2 text-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                value={selectedBranch.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="Type email here..."
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-gray-600">Street:</label>
              <input
                type="text"
                className="rounded-md border border-gray-300 px-3 py-2 text-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                value={selectedBranch.address?.street}
                onChange={(e) => handleChangeAddress("street", e.target.value)}
                placeholder="Type street here..."
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-gray-600">City:</label>
              <input
                type="text"
                className="rounded-md border border-gray-300 px-3 py-2 text-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                value={selectedBranch.address?.city}
                onChange={(e) => handleChangeAddress("city", e.target.value)}
                placeholder="Type city here..."
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-gray-600">State:</label>
              <input
                type="text"
                className="rounded-md border border-gray-300 px-3 py-2 text-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                value={selectedBranch.address?.province}
                onChange={(e) =>
                  handleChangeAddress("province", e.target.value)
                }
                placeholder="Type state here..."
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-gray-600">Country:</label>
              <input
                type="text"
                className="rounded-md border border-gray-300 px-3 py-2 text-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                value={selectedBranch.address?.country}
                onChange={(e) => handleChangeAddress("country", e.target.value)}
                placeholder="Type country here..."
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-gray-600">
                Postal Code:
              </label>
              <input
                type="text"
                className="rounded-md border border-gray-300 px-3 py-2 text-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                value={selectedBranch.address?.postal_code}
                onChange={(e) =>
                  handleChangeAddress("postal_code", e.target.value)
                }
                placeholder="Type postal code here..."
              />
            </div>
          </div>
          <div className="flex w-full justify-end">
            <div className="2xl:w-1/10 mt-4 flex w-1/2 flex-row justify-end gap-2 lg:w-1/4">
              <Button
                variant="outline"
                className="w-1/2 border-red-600 text-red-600 hover:bg-red-100"
                onClick={() => setIsEditBranchInformation(false)}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                className="w-1/2 bg-red-500 hover:bg-red-600"
                type="submit"
              >
                Save
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
